import { useEffect } from 'react';

import CustomIcon from '@components/utils/CustomIcon';
import { PlaneTakeOff } from '@assets/icons';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { SearchBarInputError } from '@components/views/flight/types';
import { useDebounceValue } from '@hooks/hooks';
import { useAirportSearchGet } from '@components/views/flight/hooks/flight.query';
import { AirportSearchViewModel } from '@api/index';
import AirportSearchResultList from '@components/views/flight/listing/SearchBar/AirportSearch/AirportSearchResultList';
import {
  AirportsContainer,
  AirportWrapper,
} from '@components/views/flight/listing/SearchBar/AirportSearch/SearchBarAirportSearchStyle';

import SearchBarSearchInput from './SearchBarSearchInput/SearchBarSearchInput';

export default function AirportSearch({
  departureAirportError,
  departureAirportSearchText,
  onDepartureAirportTextChange,
  onDepartureAirportSelect,
  departureAirportSelectError = false,
  setDepartureEntered,
  isHomePage,
  airportSelectionInputRef,
  isAirportSelectionClicked,
  setIsAirportSelectionClicked,
  setIsHotelDateSelected,
  isAirportDateSelectionStarted,
}: {
  departureAirport?: AirportSearchViewModel;
  departureAirportError?: SearchBarInputError | boolean;
  departureAirportSearchText?: string | undefined;
  onDepartureAirportTextChange?: (value: string) => void;
  setDepartureEntered?: (value: boolean) => void;
  onDepartureAirportSelect?: (
    airport: AirportSearchViewModel | undefined
  ) => void;
  departureAirportSelectError?: boolean;
  isHomePage?: boolean;
  airportSelectionInputRef?: React.RefObject<HTMLInputElement>;
  isAirportSelectionClicked?: boolean;
  setIsAirportSelectionClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isHotelDateSelected?: boolean;
  setIsHotelDateSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  isAirportDateSelectionStarted?: boolean;
  setisAirportDateSelectionStarted?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}) {
  const debouncedDepartureSearchText = useDebounceValue(
    departureAirportSearchText
  );

  useEffect(() => {
    if (isAirportSelectionClicked) {
      airportSelectionInputRef?.current?.focus();
    }
  }, [isAirportSelectionClicked]);

  const {
    data: departureAirportData,
    isInitialLoading: isDepartureAirportDataLoading,
    isError: isDepartureDataError,
  } = useAirportSearchGet({
    airportName: debouncedDepartureSearchText,
    select: (airports) => airports,
  });

  const filteredAirports = departureAirportData?.filter(
    (airport) => airport.cityCode !== 'ECN'
  );

  return (
    <AirportsContainer isHomePage={isHomePage}>
      <LocationAnimationErrorBox
        borderRadius={isHomePage ? '0' : '8px'}
        hasError={!!departureAirportError}
      >
        <AirportWrapper>
          <SearchBarSearchInput
            isFlight={true}
            isFlightIconShow={true}
            padding='16px'
            label='Nereden Gideceksiniz?'
            inputProps={{
              value: departureAirportSearchText,
              placeholder: 'Havalimanı veya Şehir',
              onChange: (e) =>
                onDepartureAirportTextChange?.(
                  (e.target as HTMLInputElement).value
                ),
              onFocus: () => {
                setDepartureEntered?.(true);
              },
            }}
            error={departureAirportSelectError}
            searchTextError={isDepartureDataError}
            leftIcon={() => (
              <CustomIcon
                fontSize={20}
                paletteColor='coral'
                icon={PlaneTakeOff}
              />
            )}
            SearchResultListComponent={({ onChange }) => (
              <AirportSearchResultList
                searchText={debouncedDepartureSearchText}
                onSelect={(item) => {
                  onChange(item?.airportName);
                  onDepartureAirportSelect?.(item);
                  if (isAirportDateSelectionStarted) {
                    setIsHotelDateSelected?.(true);
                  }
                }}
                data={filteredAirports || []}
                isLoading={isDepartureAirportDataLoading}
              />
            )}
            isHomePage={isHomePage}
            inputRef={airportSelectionInputRef}
            setIsAirportSelectionClicked={setIsAirportSelectionClicked}
            isAirportSelectionClicked={isAirportSelectionClicked}
          />
        </AirportWrapper>
      </LocationAnimationErrorBox>
    </AirportsContainer>
  );
}
